import React from 'react'
import { observer } from 'mobx-react-lite'
import { formatISO } from 'date-fns/formatISO'
import { fromUnixTime } from 'date-fns/fromUnixTime'

import Button from '@mui/material/Button'

import { Add } from '@mxenabled/mx-icons'

import {
  Drawer,
  useGlobalCopyStore,
  useRecurringTransactionsStore,
  useTransactionStore,
  useUserStore,
} from '../../../../common'
import {
  type RepeatingTransaction,
  type Transaction,
  Frequency,
  RepeatingType,
} from '../../../../core'

import { SelectTransaction } from './SelectTransaction'
import { AddRecurringTransactionDetails } from './AddRecurringTransactionDetails'

import {
  expenseFilter,
  expenseFilterSearch,
  incomeFilter,
  incomeFilterSearch,
} from '../../../../transactions/utils/Filters'

import { getNextPaymentDate } from '../../../../core/utils/RecurringTransactionsUtil'

interface AddRecurringTransactionProps {
  isIncome?: boolean
  label: string
  onAddRecurringTransaction?: () => void
}

export const AddRecurringTransaction = observer(
  ({
    isIncome = false,
    label,
    onAddRecurringTransaction = () => null,
  }: AddRecurringTransactionProps) => {
    const { recurring: copy } = useGlobalCopyStore()
    const { filter, setFilter, transactions } = useTransactionStore()
    const { addRepeatingTransaction } = useRecurringTransactionsStore()
    const { user } = useUserStore()

    const [isSelectTransactionOpen, setIsSelectTransactionOpen] = React.useState(false)
    const [transactionGuid, setTransactionGuid] = React.useState('')
    const [frequency, setFrequency] = React.useState(Frequency.EveryMonth)
    const [firstDay, setFirstDay] = React.useState(1)
    const [secondDay, setSecondDay] = React.useState(15)

    const transaction = React.useMemo(
      () => transactions.find((t) => t.guid === transactionGuid),
      [transactionGuid, transactions],
    )

    const handleOpen = () => {
      setIsSelectTransactionOpen(true)

      if (isIncome) {
        setFilter({ ...filter, custom: incomeFilter })
      } else {
        setFilter({ ...filter, custom: expenseFilter })
      }
    }

    const handleAddRecurringTransaction = async () => {
      if (!transaction) {
        closeOnAdd()
        return
      }
      onAddRecurringTransaction()

      const nextDate = getNextPaymentDate(frequency, fromUnixTime(transaction.date), firstDay)

      const repeatingTransaction = {
        account_guid: transaction.account_guid,
        amount: transaction.amount,
        category_guid: transaction.category_guid ?? null,
        created_by: 0, // User
        main_class_guid: transaction.main_class_guid ?? null,
        merchant_guid: transaction.merchant_guid,
        description: transaction.payee || transaction.description || transaction.feed_description,
        predicted_occurs_on: formatISO(nextDate, { representation: 'date' }),
        recurrence_day: firstDay,
        recurrence_type: frequency,
        repeating_transaction_type: isIncome ? RepeatingType.Income : RepeatingType.Bill,
        second_recurrence_day: secondDay,
        transaction_type: transaction.transaction_type,
        user_guid: user.guid,
      } as RepeatingTransaction

      await addRepeatingTransaction(repeatingTransaction, transaction)
      closeOnAdd()
    }

    const closeOnAdd = () => {
      setTransactionGuid('')
      setIsSelectTransactionOpen(false)
    }

    return (
      <>
        <Button onClick={handleOpen} startIcon={<Add size={24} />} variant="contained">
          {label}
        </Button>

        <Drawer
          ariaLabelClose={copy.close_drawer_aria}
          isOpen={isSelectTransactionOpen}
          onClose={() => setIsSelectTransactionOpen(false)}
          title={label}
        >
          <SelectTransaction
            description={isIncome ? copy.add_income_description : copy.add_expense_description}
            onSearch={(searchValue) => {
              setFilter({
                ...filter,
                custom: (transaction: Transaction) => {
                  if (isIncome) {
                    return searchValue
                      ? incomeFilterSearch(transaction, searchValue)
                      : incomeFilter(transaction)
                  } else {
                    return searchValue
                      ? expenseFilterSearch(transaction, searchValue)
                      : expenseFilter(transaction)
                  }
                },
              })
            }}
            onSelected={(guid) => setTransactionGuid(guid)}
          />
        </Drawer>

        <Drawer
          ariaLabelClose={copy.close_drawer_aria}
          isOpen={Boolean(transactionGuid)}
          onClose={() => setTransactionGuid('')}
          onPrimaryAction={handleAddRecurringTransaction}
          title={isIncome ? copy.income_details : copy.expense_details}
        >
          {transaction && (
            <AddRecurringTransactionDetails
              firstDay={firstDay}
              frequency={frequency}
              onFirstDayChange={setFirstDay}
              onFrequencyChange={setFrequency}
              onSecondDayChange={setSecondDay}
              secondDay={secondDay}
              transaction={transaction}
            />
          )}
        </Drawer>
      </>
    )
  },
)
